import { Vec2 } from './utils/math';
import { Component, LaserSource, FlatMirror, Lens, Prism } from './engine/Component';
import { LightEngine } from './engine/LightEngine';
import { Scene, SceneData } from './engine/Scene';
import { CanvasController, ToolMode } from './ui/CanvasController';
import { ParameterPanel } from './ui/ParameterPanel';
import { PinnedDetectorManager } from './ui/PinnedDetectors';

const AUTOSAVE_KEY = 'lighttoy_scene';
const AUTOSAVE_DEBOUNCE = 500;

const canvas = document.getElementById('simulation');
if (!(canvas instanceof HTMLCanvasElement)) {
  throw new Error('Canvas element #simulation not found');
}
const engine = new LightEngine(canvas);
const controller = new CanvasController(canvas, engine);

const pinnedDetectors = new PinnedDetectorManager(
  document.getElementById('pinned-container')!,
);

const paramPanel = new ParameterPanel(
  document.getElementById('parameter-panel')!,
  () => { engine.markDirty(); scheduleAutosave(); },
  () => engine.pushHistory(),
);
paramPanel.onPinDetector = (det) => pinnedDetectors.toggle(det);
paramPanel.isPinned = (id) => pinnedDetectors.isPinned(id);

// Link controller events to parameter panel
controller.onComponentSelected = (comp) => {
  if (comp) {
    paramPanel.show(comp);
  } else {
    paramPanel.hide();
  }
};

controller.onSceneChanged = () => scheduleAutosave();

// Toolbar buttons
document.querySelectorAll('.tool-btn').forEach(btn => {
  btn.addEventListener('click', () => {
    const tool = (btn as HTMLElement).dataset.tool as ToolMode;
    if (!tool) return; // non-tool buttons (save/load/etc)
    document.querySelectorAll('.tool-btn[data-tool]').forEach(b => b.classList.remove('active'));
    btn.classList.add('active');
    controller.setTool(tool);
  });
});

// Collapsible component groups
document.querySelectorAll('.group-toggle').forEach(toggle => {
  toggle.addEventListener('click', () => {
    toggle.classList.toggle('collapsed');
    const items = toggle.nextElementSibling as HTMLElement;
    if (items) {
      items.classList.toggle('collapsed');
    }
  });
});

// ─── Demo scene ───

function loadDemoScene(): void {
  Component.resetIdCounter();
  engine.scene.clear();
  engine.scene.add(new LaserSource(new Vec2(120, 350), -0.15, 550e-9, 1, true));
  engine.scene.add(new Prism(new Vec2(350, 320), 0.1, 70));
  engine.scene.add(new LaserSource(new Vec2(100, 150), 0, 650e-9, 1));
  engine.scene.add(new LaserSource(new Vec2(100, 100), 0.08, 450e-9, 1));
  engine.scene.add(new FlatMirror(new Vec2(550, 140), Math.PI / 4, 80));
  engine.scene.add(new Lens(new Vec2(300, 150), 0, 150, 80));
  engine.markDirty();
}

// ─── Save / Load ───

let autosaveTimer: number;

function scheduleAutosave(): void {
  clearTimeout(autosaveTimer);
  autosaveTimer = window.setTimeout(() => {
    try {
      localStorage.setItem(AUTOSAVE_KEY, JSON.stringify(engine.scene.toJSON()));
    } catch { /* quota exceeded — ignore */ }
  }, AUTOSAVE_DEBOUNCE);
}

function loadFromLocalStorage(): boolean {
  try {
    const raw = localStorage.getItem(AUTOSAVE_KEY);
    if (!raw) return false;
    const data = JSON.parse(raw) as SceneData;
    if (!data.version || !data.components) return false;
    engine.replaceScene(Scene.fromJSON(data));
    return true;
  } catch {
    return false;
  }
}

function exportScene(): void {
  const json = JSON.stringify(engine.scene.toJSON(), null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'scene.lighttoy.json';
  a.click();
  URL.revokeObjectURL(url);
}

function importScene(file: File): void {
  const reader = new FileReader();
  reader.onload = () => {
    try {
      const data = JSON.parse(reader.result as string) as SceneData;
      if (!data.version || !data.components) return;
      engine.replaceScene(Scene.fromJSON(data));
      paramPanel.hide();
      scheduleAutosave();
    } catch { /* invalid file — ignore */ }
  };
  reader.readAsText(file);
}

// Scene management buttons
document.getElementById('btn-new-scene')!.addEventListener('click', () => {
  loadDemoScene();
  engine.history.clear();
  paramPanel.hide();
  scheduleAutosave();
});

document.getElementById('btn-save')!.addEventListener('click', exportScene);

const fileInput = document.getElementById('file-input') as HTMLInputElement;
document.getElementById('btn-load')!.addEventListener('click', () => fileInput.click());
fileInput.addEventListener('change', () => {
  if (fileInput.files?.[0]) {
    importScene(fileInput.files[0]);
    fileInput.value = ''; // allow re-import of same file
  }
});

// Also support drag-and-drop onto canvas
canvas.addEventListener('dragover', (e) => { e.preventDefault(); e.dataTransfer!.dropEffect = 'copy'; });
canvas.addEventListener('drop', (e) => {
  e.preventDefault();
  const file = e.dataTransfer?.files[0];
  if (file && file.name.endsWith('.json')) {
    importScene(file);
  }
});

// ─── Load saved scene or demo ───
if (!loadFromLocalStorage()) {
  loadDemoScene();
}

// FDTD toggle
const fdtdToggle = document.getElementById('fdtd-toggle')!;
fdtdToggle.addEventListener('click', () => {
  engine.fdtdEnabled = !engine.fdtdEnabled;
  fdtdToggle.classList.toggle('active', engine.fdtdEnabled);
});

const debugToggle = document.getElementById('debug-toggle')!;
debugToggle.addEventListener('click', () => {
  engine.toggleFieldDebug();
  debugToggle.classList.toggle('active');
});

// Grid toggle
const gridToggle = document.getElementById('grid-toggle')!;
gridToggle.addEventListener('click', () => {
  engine.toggleGrid();
  gridToggle.classList.toggle('active', engine.gridEnabled);
});

// Performance HUD
const hud = document.getElementById('performance-hud')!;
let frameCount = 0;
let lastFpsTime = performance.now();

function updateHud(): void {
  frameCount++;
  const now = performance.now();
  if (now - lastFpsTime >= 1000) {
    const fps = Math.round(frameCount * 1000 / (now - lastFpsTime));
    const segments = engine.tracedSegments.length;
    const components = engine.scene.count;
    const grids = engine.activeFieldGrids.length;
    const fdtdLabel = engine.fdtdEnabled ? ` | ${grids} fields` : '';
    hud.textContent = `${fps} fps | ${segments} rays | ${components} components${fdtdLabel}`;
    frameCount = 0;
    lastFpsTime = now;
  }
  requestAnimationFrame(updateHud);
}

// Keyboard shortcuts
document.addEventListener('keydown', (e) => {
  if (e.key === 'Delete' || e.key === 'Backspace') {
    const id = engine.selectedId;
    if (id !== undefined) {
      engine.pushHistory();
      engine.scene.remove(id);
      engine.selectedId = undefined;
      engine.markDirty();
      paramPanel.hide();
      scheduleAutosave();
    }
  }
  if (e.key === 'Escape') {
    controller.setTool('select');
    document.querySelectorAll('.tool-btn[data-tool]').forEach(b => b.classList.remove('active'));
    document.querySelector('[data-tool="select"]')?.classList.add('active');
  }
  if (e.key === 'f' || e.key === 'F') {
    engine.fdtdEnabled = !engine.fdtdEnabled;
    fdtdToggle.classList.toggle('active', engine.fdtdEnabled);
  }
  if (e.key === 'a' || e.key === 'A') {
    engine.toggleFieldViewMode();
  }
  if (e.key === 'b' || e.key === 'B') {
    engine.toggleBloom();
  }
  if (e.key === 'g' && !e.shiftKey) {
    engine.toggleGrid();
    gridToggle.classList.toggle('active', engine.gridEnabled);
  }
  if (e.key === 'G' && e.shiftKey) {
    engine.cycleGridSpacing();
  }
  if (e.key === 'p' || e.key === 'P') {
    engine.togglePhaseColor();
  }
  // Copy/Paste
  if ((e.ctrlKey || e.metaKey) && e.key === 'c') {
    controller.copySelected();
  }
  if ((e.ctrlKey || e.metaKey) && e.key === 'v') {
    e.preventDefault();
    controller.pasteAtMouse();
    scheduleAutosave();
  }
  // Reset camera view
  if (e.key === '0' && (e.ctrlKey || e.metaKey)) {
    e.preventDefault();
    engine.camera.reset();
    engine.markDirty();
  }
  // Undo/Redo
  if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
    e.preventDefault();
    if (engine.undo()) {
      paramPanel.hide();
      scheduleAutosave();
    }
  }
  if ((e.ctrlKey || e.metaKey) && (e.key === 'Z' || e.key === 'y')) {
    e.preventDefault();
    if (engine.redo()) {
      paramPanel.hide();
      scheduleAutosave();
    }
  }
});

engine.start();
updateHud();
